/**
 * useQuickLog — the "quick log" mode: one tap on the map or the route list
 * records a climb, no modal.
 *
 * Why it exists: logging a 25-route warm-up lap one RouteModal at a time is
 * unusable, and that lap is exactly the volume the repeat feature is meant to
 * capture. Without this, repeats technically work but nobody logs them.
 *
 * The mode changes what a tap MEANS, which is the risky part of any modal UI.
 * Three things keep it honest:
 *   - it is never silently on (the bar is visible for as long as it is active),
 *   - it turns itself off rather than surviving into a context where a tap
 *     means something else (route placing, a running competition session, the
 *     next day, or three idle hours),
 *   - the bar itself is the feedback surface. There is deliberately no toast
 *     per tap: 25 taps would mean 25 toasts.
 *
 * NOT named "session" (taken: `competitions/{id}/sessions`) and not "training
 * mode" (that is the separate, deferred training PLAN).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { climbsForRoute } from "../utils/climbLog";
import { sessionStore } from "../utils/safeStorage";

/** Turn itself off after this long without a tap, so it never greets you
 *  tomorrow still armed. */
export const QUICK_LOG_IDLE_MS = 3 * 60 * 60 * 1000;

const storageKey = (gymId) => `sendly.quickLog.${gymId || "none"}`;

/**
 * @param {object}   opts
 * @param {string}   opts.gymId
 * @param {string}   opts.todayKey     gym-timezone "YYYY-MM-DD"; a change ends the mode
 * @param {boolean}  opts.blocked      route placing / running comp session
 * @param {Function} opts.logClimb     from useMainApp; called with { silent: true }
 * @param {Function} opts.removeClimb  single-entry removal, for undoing a repeat
 * @param {Function} opts.removeRouteClimbs  all-or-nothing removal, for undoing a send
 */
export function useQuickLog({ gymId, todayKey, blocked, logClimb, removeClimb, removeRouteClimbs }) {
  // sessionStorage, not state alone: a PWA can restart mid-session (iOS evicts
  // backgrounded tabs), and coming back to a silently-disarmed map would be
  // worse than coming back to an armed one — the bar says which it is.
  const [active, setActive] = useState(() => sessionStore.get(storageKey(gymId)) === "1");
  // { routeId, name, kind, cid, date } | null — what the bar echoes back.
  const [lastAction, setLastAction] = useState(null);
  // { routeId, name } | null — the double-tap guard fired; say so.
  const [cooldownHit, setCooldownHit] = useState(null);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    // A failed write (private mode) is fine: the in-memory state still works.
    if (active) sessionStore.set(storageKey(gymId), "1");
    else sessionStore.remove(storageKey(gymId));
  }, [active, gymId]);

  const stop = useCallback(() => {
    setActive(false);
    setLastAction(null);
    setCooldownHit(null);
  }, []);

  // Route placing and a running competition session both redefine what tapping
  // the map does. Two such modes cannot be armed at once, so this one yields.
  useEffect(() => { if (blocked && active) stop(); }, [blocked, active, stop]);

  // Midnight rolls the daily log doc over, and switching gyms changes which
  // wall a tap refers to; end the mode on either rather than letting an armed
  // map carry into a context the counter no longer describes.
  //
  // `stop` is intentionally NOT a dependency: it is a stable useCallback, and
  // listing it would not change when this runs — the trigger is the day/gym.
  // Skipped on the first run: mount is not a day/gym CHANGE, and clearing there
  // would defeat the sessionStorage restore above (a PWA that restarted
  // mid-session would silently come back disarmed).
  const resetRef = useRef(stop);
  resetRef.current = stop;
  const resetPrimedRef = useRef(false);
  useEffect(() => {
    if (!resetPrimedRef.current) { resetPrimedRef.current = true; return; }
    resetRef.current();
  }, [todayKey, gymId]);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => stop(), QUICK_LOG_IDLE_MS);
  }, [stop]);

  useEffect(() => {
    if (!active) {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      return undefined;
    }
    armIdleTimer();
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
  }, [active, armIdleTimer]);

  const start = useCallback(() => {
    if (blocked) return;
    setActive(true);
    setLastAction(null);
    setCooldownHit(null);
  }, [blocked]);

  const toggle = useCallback(() => { if (active) stop(); else start(); }, [active, start, stop]);

  /**
   * Log one climb from a tap. Returns true when the tap was consumed, so the
   * caller knows not to fall through to opening the route modal.
   */
  const tapRoute = useCallback(async (route) => {
    if (!active || !route || blocked) return false;
    armIdleTimer();
    setCooldownHit(null);
    // `silent` suppresses the per-tap toast AND the cooldown toast: at this
    // volume the bar is the right place for both.
    const res = await logClimb(route.id, { silent: true });
    if (!res) return true;
    if (res.blocked) {
      setCooldownHit({ routeId: route.id, name: route.name || "" });
      return true;
    }
    setLastAction({ routeId: route.id, name: route.name || "", kind: res.kind, cid: res.cid, date: res.date });
    return true;
  }, [active, blocked, logClimb, armIdleTimer]);

  /**
   * Undo the last tap — the bar's ↩ button.
   *
   * A SEND has to go through the all-or-nothing path: its `ascents` doc and its
   * log entry were written in one batch, so removing only the log entry would
   * leave the route marked climbed with no climb behind it.
   */
  const undoLast = useCallback(async () => {
    if (!lastAction) return;
    if (lastAction.kind === "send") await removeRouteClimbs(lastAction.routeId, { silent: true });
    else await removeClimb(lastAction.date, lastAction.cid);
    setLastAction(null);
  }, [lastAction, removeClimb, removeRouteClimbs]);

  /**
   * Take back the newest of TODAY's entries for one route — the "−" button on
   * a row of the bar's expanded list.
   *
   * Same asymmetry as `undoLast`: when that newest entry is the SEND, the
   * route has no other climb at all (a send is always the first), so the only
   * consistent removal is the all-or-nothing one. A plain `removeClimb` there
   * left the `ascents` doc behind — the route stayed "climbed" with no log
   * entry, and the day's totals drifted from gymStats.
   *
   * If the removed entry is the one the ↩ button points at, that undo is
   * dropped too: replaying it would remove the same climb twice (and, for a
   * send, decrement the optimistic ascent count a second time).
   */
  const removeLastForRoute = useCallback(async (todayClimbLog, routeId) => {
    const list = climbsForRoute(todayClimbLog?.climbs || {}, routeId);
    const last = list[list.length - 1];
    if (!last) return;
    if (last.kind === "send") await removeRouteClimbs(routeId, { silent: true });
    else await removeClimb(todayClimbLog.date, last.cid);
    setLastAction(a => (a && a.cid === last.cid ? null : a));
  }, [removeClimb, removeRouteClimbs]);

  return { active, start, stop, toggle, tapRoute, lastAction, cooldownHit, undoLast, removeLastForRoute };
}
